import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Row, Col, Card, Statistic, Table, Typography, Alert, Spin, Empty,
} from 'antd';
import {
  EyeOutlined, ProfileOutlined, ShareAltOutlined, MessageOutlined,
} from '@ant-design/icons';
import { analyticsService } from '../../services/api';
import LineChart from '../../components/LineChart';

const { Title, Text } = Typography;

/**
 * Métricas de UN catálogo de genética, dentro de la pantalla de Analytics.
 *
 * VIVIA ADENTRO DEL PANEL DEL CATALOGO y se mudó acá. El argumento de entonces
 * era "el productor que quiere saber si su catálogo camina ya está en esa
 * pantalla", y eso optimiza un click a costa del modelo mental: con las
 * métricas en un solo lugar el productor aprende UNA vez dónde se miran los
 * números; con dos, cada vez tiene que recordar cuál mirar según qué quiera
 * ver. Textual del usuario: *"tener dos lugares diferentes donde ver analytics
 * confunde, no educa"*.
 *
 * NO es el tablero de un remate con otros datos: un catálogo no tiene ofertas,
 * ni ventas por categoría, ni invitados que vean una parte. Por eso es un
 * componente aparte y no un `if` adentro del tablero.
 */
// `encendido` y no `activo`: en `CatalogoGenetica.jsx` la variable `activo` es
// EL CATALOGO SELECCIONADO, no el flag de publicado. Dos cosas distintas con el
// mismo nombre a un import de distancia es como se cuelan los bugs tontos.
const MetricasCatalogo = ({ idCatalogo, encendido, dias = 30 }) => {
  const navigate = useNavigate();
  // `dias` VIENE DE AFUERA: lo manda el mismo control que usa el tablero del
  // remate, asi la pantalla tiene UNA sola ventana de tiempo.
  //
  // El 0 de "Todo" se traduce a 365 porque el endpoint acota a [1, 365]: sin
  // esta linea, pedir "Todo" devolveria una serie de UN dia —el clamp lo lleva
  // al minimo— y el grafico mostraria casi nada justo cuando se pidio todo.
  const diasEfectivos = dias === 0 ? 365 : dias;
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  // Un 403 y un 500 no son la misma pantalla: el primero es "tu plan no
  // incluye métricas" y el segundo es "volvé a intentar". Colapsarlos manda al
  // productor a pedir soporte por algo que su plan dice claramente.
  const [error, setError] = useState(null);

  const cargar = useCallback(async () => {
    // EL QUE ABORTA, APAGA. Hoy el tab nunca se monta sin id, pero un
    // `return` temprano que se va sin bajar la bandera deja el spinner
    // girando PARA SIEMPRE sobre una pantalla vacía — es el mismo pozo que
    // `cargarCandidatos` documenta en `CatalogoGenetica.jsx`.
    if (!idCatalogo) {
      setCargando(false);
      return;
    }
    setCargando(true);
    setError(null);
    // Y LOS DATOS TAMBIEN SE LIMPIAN. Al cambiar de catalogo en el selector,
    // este componente NO se desmonta: solo le cambia el `idCatalogo`. Sin el
    // reset, el guard de abajo (`cargando && !datos`) no entra —`datos` todavia
    // tiene lo anterior— y la pantalla muestra las 1.200 visitas de un catalogo
    // con el nombre de otro, mientras el cartel de "esta apagado" ya cambio.
    //
    // Una foto equivocada se nota; un NUMERO equivocado no. Ese es el motivo de
    // que esto importe, y es el mismo reset que ya esta escrito en la ficha del
    // animal (`AnimalCatalogoPage`, `setData(null)`).
    setDatos(null);
    try {
      const res = await analyticsService.getStatsCatalogo(idCatalogo, diasEfectivos);
      setDatos(res.data);
    } catch (err) {
      // Sin `message.error`: excepción (e) de react-rules.md. El error se
      // pinta como Alert PERSISTENTE en el lugar de los datos, distinguiendo
      // 403 ("tu plan no incluye esto") de 500 ("se rompió"). Un toast de
      // tres segundos sobre una pantalla vacía dice menos y se va antes.
      setDatos(null);
      setError(err.response?.status === 403 ? 'sin_permiso' : 'server_error');
    } finally {
      setCargando(false);
    }
  }, [idCatalogo, diasEfectivos]);

  useEffect(() => { cargar(); }, [cargar]);

  if (cargando && !datos) {
    return <div style={{ padding: 48, textAlign: 'center' }}><Spin size="large" /></div>;
  }

  if (error === 'sin_permiso') {
    return (
      <Alert
        type="info"
        showIcon
        message="Tu plan no incluye métricas"
        description="El catálogo funciona igual y sigue registrando las visitas. Cuando actives el módulo de métricas, vas a ver acá todo lo que se acumuló."
      />
    );
  }

  if (error || !datos) {
    return (
      <Alert
        type="error"
        showIcon
        message="No pudimos cargar las métricas"
        description="Puede ser algo pasajero. Probá de nuevo en un momento."
      />
    );
  }

  const r = datos.resumen;
  const huboAlgo = r.visitas_index + r.visitas_animal + r.compartidos + (r.consultas ?? 0) > 0;

  return (
    <>
      {/* Sin `<Row justify="space-between">`: quedaba una sola columna y un
          hueco donde estaba el selector de dias, que se fue al control general
          de la pantalla. */}
      <div style={{ marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>Métricas del catálogo</Title>
        <Text type="secondary">
          Los totales son desde siempre. La ventana recorta sólo el gráfico.
        </Text>
      </div>

      {/* El catálogo APAGADO no es un error, pero explica un cero que si no
          desconcierta: no hay visitas porque la página no está publicada. */}
      {!encendido && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Este catálogo está apagado"
          description="Mientras esté apagado su página pública devuelve 404, así que no va a registrar visitas nuevas."
        />
      )}

      {/* CUATRO tarjetas y no cinco. `visitas_categoria` viaja en la respuesta
          —el tipo existe en el esquema y el endpoint lo acepta— pero HOY el
          catálogo no lo emite: `trackCatalogo` sólo manda `visita_index`,
          `visita_animal` y `compartir_animal`, y la página pública del
          catálogo ni siquiera tiene filtro de categoría (por eso el panel le
          pasa `ocultar={['filtro_posicion']}` a `EstructuraTab`).

          Una tarjeta con un cero estructural se lee como "nadie usó los
          filtros", no como "esto no está conectado" — y ese es exactamente el
          modo de falla silenciosa que este código evita en todos lados. Cuando
          el catálogo tenga filtro y lo registre, la tarjeta vuelve.

          `visitas_categoria` sigue faltando, pero ya no falta `consultas`:
          el sistema de consultas del catálogo (WhatsApp o mensajería interna)
          SÍ está conectado de punta a punta, así que esta tarjeta pasó de
          "nadie usó el filtro" a un número real. */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card variant="borderless">
            <Statistic
              title="Visitas a la portada"
              value={r.visitas_index}
              prefix={<EyeOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card variant="borderless">
            <Statistic
              title="Fichas de animales vistas"
              value={r.visitas_animal}
              prefix={<ProfileOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card variant="borderless">
            <Statistic
              title="Compartidos por WhatsApp"
              value={r.compartidos}
              prefix={<ShareAltOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card variant="borderless">
            <Statistic
              title="Consultas recibidas"
              value={r.consultas ?? 0}
              prefix={<MessageOutlined />}
            />
            <Text
              type="secondary"
              style={{ fontSize: 12, cursor: 'pointer' }}
              onClick={() => navigate('/herramientas/consultas-catalogo')}
            >
              Ver consultas →
            </Text>
          </Card>
        </Col>
      </Row>

      {!huboAlgo ? (
        <Card style={{ marginTop: 16 }}>
          <Empty
            description={
              <>
                <div>Todavía no hay visitas registradas.</div>
                <Text type="secondary">
                  Compartí el link de tu catálogo y volvé a mirar acá en unos días.
                </Text>
              </>
            }
          />
        </Card>
      ) : (
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} lg={14}>
            <Card title={`Fichas vistas por día — últimos ${datos.dias} días`} variant="borderless">
              <LineChart data={datos.visitas_por_dia} />
            </Card>
          </Col>
          <Col xs={24} lg={10}>
            <Card title="Los más mirados" variant="borderless">
              <Table
                dataSource={datos.top_animales}
                rowKey="id_animal"
                pagination={false}
                size="small"
                locale={{ emptyText: 'Ninguna ficha visitada todavía' }}
                columns={[
                  {
                    title: 'Animal',
                    dataIndex: 'nombre',
                    // El nombre viene resuelto del backend: no hay que cruzar
                    // ids contra la lista de animales para dibujar esta tabla.
                    render: (v) => <strong>{v}</strong>,
                  },
                  {
                    title: 'Visitas',
                    dataIndex: 'visitas',
                    align: 'right',
                    width: 90,
                  },
                ]}
              />
            </Card>
          </Col>
        </Row>
      )}
    </>
  );
};

export default MetricasCatalogo;
